import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const EMPTY_FORM = {
  ID: null,
  AD: "",
  ACIKLAMA: "",
  GORSEL_YOLU: "",
};

const HizmetSektorleri = () => {
  document.title = "Hizmet Sektörleri | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/HIZMET_SEKTORLERI?$orderby=AD asc`, { headers });
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("Hizmet sektörleri yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchListe(); }, []);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setModal(true);
  };

  const handleEdit = (row) => {
    setFormData({
      ID: row.ID,
      AD: row.AD || "",
      ACIKLAMA: row.ACIKLAMA || "",
      GORSEL_YOLU: row.GORSEL_YOLU || "",
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.AD.trim()) {
      setError("Sektör adı zorunludur.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        AD: formData.AD.trim(),
        ACIKLAMA: formData.ACIKLAMA || null,
        GORSEL_YOLU: formData.GORSEL_YOLU || null,
      };

      if (isEditing) {
        await fetch(`${BASE_URL}/HIZMET_SEKTORLERI(${formData.ID})`, {
          method: "PATCH", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Hizmet sektörü güncellendi.");
      } else {
        await fetch(`${BASE_URL}/HIZMET_SEKTORLERI`, {
          method: "POST", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Hizmet sektörü eklendi.");
      }

      setModal(false);
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu hizmet sektörünü silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/HIZMET_SEKTORLERI(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Hizmet sektörü silindi.");
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "#",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "Görsel",
      accessorKey: "GORSEL_YOLU",
      enableSorting: false,
      cell: (c) => c.getValue()
        ? <img src={c.getValue()} alt="" style={{ width: 42, height: 42, objectFit: "cover", borderRadius: 6 }} />
        : <span className="text-muted">—</span>,
    },
    {
      header: "Sektör Adı",
      accessorKey: "AD",
      enableSorting: true,
      cell: (c) => <span className="fw-medium">{c.getValue() || "---"}</span>,
    },
    {
      header: "Açıklama",
      accessorKey: "ACIKLAMA",
      enableSorting: false,
      cell: (c) => {
        const v = c.getValue() || "";
        return <span className="text-muted">{v.length > 80 ? v.slice(0, 80) + "…" : (v || "---")}</span>;
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const row = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(row)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(row.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="İçerik Yönetimi" breadcrumbItem="Hizmet Sektörleri" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="card-title mb-1">Hizmet Sektörleri</h4>
                      <p className="text-muted small mb-0">
                        Kullanıcı ve ilanlarda kullanılan hizmet sektörlerini buradan yönetin.
                      </p>
                    </div>
                    <Button color="primary" size="sm" onClick={openAdd}>
                      <i className="bx bx-plus me-1" />Yeni Sektör
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Sektör ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <i className={`mdi ${isEditing ? "mdi-briefcase-edit-outline" : "mdi-briefcase-plus-outline"} me-2 text-primary`}></i>
          {isEditing ? `Sektör Düzenle (#${formData.ID})` : "Yeni Sektör Ekle"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Sektör Adı <span className="text-danger">*</span></Label>
                <Input
                  value={formData.AD}
                  onChange={(e) => f("AD", e.target.value)}
                  placeholder="Örn: Hayvancılık"
                  maxLength={150}
                />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Açıklama</Label>
                <Input
                  type="textarea"
                  rows={4}
                  value={formData.ACIKLAMA}
                  onChange={(e) => f("ACIKLAMA", e.target.value)}
                  placeholder="Sektör açıklaması"
                />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Görsel URL</Label>
                <Input
                  value={formData.GORSEL_YOLU}
                  onChange={(e) => f("GORSEL_YOLU", e.target.value)}
                  placeholder="https://..."
                />
                {formData.GORSEL_YOLU && (
                  <img src={formData.GORSEL_YOLU} alt="önizleme" className="mt-2" style={{ maxHeight: 80, borderRadius: 6 }} />
                )}
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default HizmetSektorleri;
